import { useQueries, useQuery } from "@tanstack/react-query";
import {
  fetchHeroes,
  fetchAllHeroAblts,
  fetchHNrmlAblts,
  fetchHSpclAblts,
  fetchItems,
  fetchTeams,
  fetchHeroesPlayed,
  fetchTotals,
  fetchPeers,
  fetchMatches,
  fetchRankedMatchesByDate,
} from "../resourcepaths";
import { epochToTime, sortByKey } from "../../components/utilities/Custom";
import {
  filterItems,
  filterNeutralItems,
} from "../../components/items/helpers";

export const useInitData = () => {
  return useQueries({
    queries: [
      {
        queryKey: ["heroes"],
        queryFn: fetchHeroes,
        staleTime: Infinity,
        select: (data) => {
          let heroes = {};
          heroes.allHeroes = data;
          heroes.strHeroes = [];
          heroes.agiHeroes = [];
          heroes.intHeroes = [];
          let heroArray = sortByKey(Object.values(data), "localized_name");
          for (let hero in heroArray) {
            heroArray[hero].blurImg = false;
            switch (heroArray[hero].primary_attr) {
              case "str":
                heroes.strHeroes = [...heroes.strHeroes, heroArray[hero]];
                break;
              case "agi":
                heroes.agiHeroes = [...heroes.agiHeroes, heroArray[hero]];
                break;
              default:
                heroes.intHeroes = [...heroes.intHeroes, heroArray[hero]];
                break;
            }
          }
          return heroes;
        },
      },
      {
        queryKey: ["items"],
        queryFn: fetchItems,
        staleTime: Infinity,
        select: (data) => {
          let items = {};
          items.allItems = data;
          items.basicItems = [];
          items.upgradedItems = [];
          items.neutralItems = [];
          let itemArray = sortByKey(Object.values(items.allItems), "cost");
          for (let item in itemArray) {
            if (itemArray[item].dname !== undefined) {
              if (itemArray[item].tier !== undefined) {
                items.neutralItems = [...items.neutralItems, itemArray[item]];
              } else if (itemArray[item].components === null) {
                if (itemArray[item].dname.indexOf("River") === -1) {
                  items.basicItems = [...items.basicItems, itemArray[item]];
                }
              } else {
                if (itemArray[item].qual !== undefined) {
                  items.upgradedItems = [
                    ...items.upgradedItems,
                    itemArray[item],
                  ];
                }
              }
            }
          }
          items.basicItems = filterItems(items.basicItems, "qual");
          items.upgradedItems = filterItems(items.upgradedItems, "qual");
          items.neutralItems = filterNeutralItems(items.neutralItems);
          return items;
        },
      },
      {
        queryKey: ["teams"],
        queryFn: fetchTeams,
        staleTime: Infinity,
        select: (data) => {
          let results = data.map((rslt) => {
            return {
              logo_url: rslt.logo_url,
              name: rslt.name,
              tag: rslt.tag,
              last_match_time:
                rslt.last_match_time !== undefined
                  ? epochToTime(rslt.last_match_time)
                  : "---",
              losses: rslt.losses,
              wins: rslt.wins,
            };
          });
          return results;
        },
      },
    ],
  });
};

export const useHeroesData = () => {
  return useQuery(["heroes"], fetchHeroes, {
    staleTime: Infinity,
    select: (data) => {
      let heroes = {};
      heroes.allHeroes = data;
      heroes.strHeroes = [];
      heroes.agiHeroes = [];
      heroes.intHeroes = [];
      heroes.uniHeroes = [];

      let heroArray = sortByKey(Object.values(data), "localized_name");
      for (let hero in heroArray) {
        heroArray[hero].blurImg = false;
        switch (heroArray[hero].primary_attr) {
          case "str":
            heroes.strHeroes = [...heroes.strHeroes, heroArray[hero]];
            break;
          case "agi":
            heroes.agiHeroes = [...heroes.agiHeroes, heroArray[hero]];
            break;
          case "int":
            heroes.intHeroes = [...heroes.intHeroes, heroArray[hero]];
            break;
          default:
            heroes.uniHeroes = [...heroes.uniHeroes, heroArray[hero]];
            break;
        }
      }
      return heroes;
    },
  });
};

export const useAllHeroAbltsData = () => {
  return useQueries({
    queries: [
      {
        queryKey: ["allHeroAblts"],
        queryFn: fetchAllHeroAblts,
        staleTime: Infinity,
      },
      {
        queryKey: ["hNrmlAblts"],
        queryFn: fetchHNrmlAblts,
        staleTime: Infinity,
      },
      {
        queryKey: ["hSpclAblts"],
        queryFn: fetchHSpclAblts,
        staleTime: Infinity,
      },
    ],
  });
};

export const useItemsData = () => {
  return useQuery(["items"], fetchItems, {
    staleTime: Infinity,
    select: (data) => {
      let items = {};
      items.allItems = data;
      items.basicItems = [];
      items.upgradedItems = [];
      items.neutralItems = [];
      let itemArray = sortByKey(Object.values(items.allItems), "cost");
      for (let item in itemArray) {
        if (itemArray[item].dname !== undefined) {
          if (itemArray[item].tier !== undefined) {
            items.neutralItems = [...items.neutralItems, itemArray[item]];
          } else if (itemArray[item].components === null) {
            if (itemArray[item].dname.indexOf("River") === -1) {
              items.basicItems = [...items.basicItems, itemArray[item]];
            }
          } else {
            if (itemArray[item].qual !== undefined) {
              items.upgradedItems = [...items.upgradedItems, itemArray[item]];
            }
          }
        }
      }
      items.basicItems = filterItems(items.basicItems, "qual");
      items.upgradedItems = filterItems(items.upgradedItems, "qual");
      items.neutralItems = filterNeutralItems(items.neutralItems);
      return items;
    },
  });
};

export const usePlayerHeroStatsData = (account_id) => {
  return useQuery(
    ["heroesPlayed", account_id],
    () => fetchHeroesPlayed(account_id),
    {
      staleTime: 60 * 1000,
    }
  );
};

export const usePlayerTotalStatsData = (account_id) => {
  return useQuery(["totalStats", account_id], () => fetchTotals(account_id), {
    staleTime: 60 * 1000,
    select: (data) => {
      let totalsSmmry = {};
      for (var item of data) {
        totalsSmmry[item.field] = item.sum;
        if (item.field === "kills") {
          totalsSmmry[`games_played`] = item.n;
        }
      }
      return totalsSmmry;
    },
  });
};

export const usePlayerPeersData = (account_id) => {
  return useQuery(["peers", account_id], () => fetchPeers(account_id), {
    staleTime: 60 * 1000,
    select: (data) => {
      let peers = data.map((peer) => {
        return {
          account_id: peer.account_id,
          personaname: peer.personaname,
          avatarfull: peer.avatarfull,
          last_played: epochToTime(peer.last_played),
          games: peer.games,
          with_win: peer.with_win,
          win_rate:
            peer.with_games === 0
              ? "0%"
              : Math.round((100 * peer.with_win) / peer.with_games) + "%",
          with_games: peer.with_games,
          against_win: peer.against_win,
          against_games: peer.against_games,
          avg_gpm: Math.round(peer.with_gpm_sum / peer.with_games),
          avg_xpm: Math.round(peer.with_xpm_sum / peer.with_games),
        };
      });
      return peers;
    },
  });
};

export const usePlayerMatchesData = (account_id) => {
  return useQuery(
    ["matchesPlayed", account_id],
    () => fetchMatches(account_id),
    {
      staleTime: 60 * 1000,
    }
  );
};

export const usePlayerRankedMatchesData = (account_id, queryParams) => {
  return useQuery(
    ["rankedMatchesByDate", account_id, queryParams],
    () => fetchRankedMatchesByDate(account_id, queryParams),
    {
      staleTime: 60 * 1000,
    }
  );
};
